import React, { useCallback, useEffect, useMemo, useState } from "react";
import useAxios from "../../utilities/useAxios";
import {
	Button,
	Card,
	Form,
	Modal,
	Popconfirm,
	Select,
	Space,
	Table,
	Typography,
	message,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import useIsMobile from "../../hooks/useIsMobile";

const { Title, Text } = Typography;

const WEEKDAY_OPTIONS = [
	{ value: 6, label: "일" },
	{ value: 0, label: "월" },
	{ value: 1, label: "화" },
	{ value: 2, label: "수" },
	{ value: 3, label: "목" },
	{ value: 4, label: "금" },
	{ value: 5, label: "토" },
];

const UserWeekdayTerminalAllowances = ({ terminals = [], loading: parentLoading }) => {
	const isMobile = useIsMobile();
	const [rows, setRows] = useState([]);
	const [employees, setEmployees] = useState([]);
	const [loading, setLoading] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);
	const [editing, setEditing] = useState(null);
	const [form] = Form.useForm();

	const terminalLabelById = useMemo(() => {
		const map = new Map();
		terminals.forEach((item) => {
			map.set(item.id, `${item.name} (${item.terminal_id})`);
		});
		return map;
	}, [terminals]);

	const employeeOptions = useMemo(
		() =>
			[...employees]
				.sort((a, b) =>
					String(a.user_name || "").localeCompare(
						String(b.user_name || ""),
						"ko",
					),
				)
				.map((item) => ({
					value: item.user_id,
					label: item.user_name || `ID ${item.user_id}`,
				})),
		[employees],
	);

	const loadRows = useCallback(async () => {
		try {
			setLoading(true);
			const res = await useAxios.get("/attendance/user-weekday-terminals/");
			setRows(res.data || []);
		} catch {
			message.error("요일별 개인 단말기 설정을 불러오지 못했습니다.");
		} finally {
			setLoading(false);
		}
	}, []);

	const loadEmployees = useCallback(async () => {
		try {
			const res = await useAxios.get("/users/employee-info/");
			setEmployees(res.data || []);
		} catch {
			message.error("교직원 목록을 불러오지 못했습니다.");
		}
	}, []);

	useEffect(() => {
		loadRows();
		loadEmployees();
	}, [loadRows, loadEmployees]);

	const openCreate = () => {
		setEditing(null);
		form.resetFields();
		setModalOpen(true);
	};

	const openEdit = (record) => {
		setEditing(record);
		form.setFieldsValue({
			user: record.user,
			weekday: record.weekday,
			allowed_terminal_ids: record.allowed_terminal_ids || [],
		});
		setModalOpen(true);
	};

	const saveRow = async () => {
		try {
			const values = await form.validateFields();
			const payload = {
				user: values.user,
				weekday: values.weekday,
				allowed_terminal_ids: values.allowed_terminal_ids || [],
			};
			if (editing) {
				await useAxios.put(
					`/attendance/user-weekday-terminals/${editing.id}/`,
					payload,
				);
				message.success("설정이 수정되었습니다.");
			} else {
				await useAxios.post("/attendance/user-weekday-terminals/", payload);
				message.success("설정이 추가되었습니다.");
			}
			setModalOpen(false);
			loadRows();
		} catch (err) {
			if (err?.errorFields) return;
			message.error(
				err?.response?.data?.error ||
					err?.response?.data?.allowed_terminal_ids?.[0] ||
					"저장에 실패했습니다.",
			);
		}
	};

	const deleteRow = async (record) => {
		try {
			await useAxios.delete(
				`/attendance/user-weekday-terminals/${record.id}/`,
			);
			message.success("설정을 삭제했습니다.");
			loadRows();
		} catch {
			message.error("삭제에 실패했습니다.");
		}
	};

	const columns = [
		{
			title: "교직원",
			dataIndex: "user_name",
			width: 140,
		},
		{
			title: "요일",
			dataIndex: "weekday_label",
			width: 72,
		},
		{
			title: "추가 허용 단말기",
			key: "terminals",
			render: (_, record) => {
				const ids = record.allowed_terminal_ids || [];
				if (!ids.length) return "-";
				return ids
					.map((id) => terminalLabelById.get(id) || id)
					.join(", ");
			},
		},
		{
			title: "작업",
			key: "actions",
			width: 160,
			render: (_, record) => (
				<Space>
					<Button type="link" onClick={() => openEdit(record)}>
						수정
					</Button>
					<Popconfirm
						title="이 설정을 삭제할까요?"
						okText="삭제"
						cancelText="취소"
						onConfirm={() => deleteRow(record)}>
						<Button type="link" danger>
							삭제
						</Button>
					</Popconfirm>
				</Space>
			),
		},
	];

	return (
		<Card
			loading={loading || parentLoading}
			title={<Title level={4}>요일별 개인 허용 단말기</Title>}
			extra={
				<Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
					설정 추가
				</Button>
			}>
			<Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
				해당 요일에는 아래 단말기에서도 출근 체크가 인정됩니다. (그날의
				공통 허용 단말기와 합쳐집니다.)
			</Text>
			<Table
				rowKey="id"
				columns={columns}
				dataSource={rows}
				pagination={false}
				sticky={{ offsetHeader: 64 }}
			/>
			<Modal
				title={editing ? "개인 단말기 설정 수정" : "개인 단말기 설정 추가"}
				open={modalOpen}
				onCancel={() => setModalOpen(false)}
				onOk={saveRow}
				okText="저장"
				cancelText="취소"
				destroyOnClose>
				<Form form={form} layout="vertical">
					<Form.Item
						name="user"
						label="교직원"
						rules={[{ required: true, message: "교직원을 선택하세요" }]}>
						<Select
							showSearch
							optionFilterProp="label"
							disabled={Boolean(editing)}
							placeholder="교직원 선택"
							options={employeeOptions}
							style={{
								width: isMobile ? "100%" : undefined,
							}}
						/>
					</Form.Item>
					<Form.Item
						name="weekday"
						label="요일"
						rules={[{ required: true, message: "요일을 선택하세요" }]}>
						<Select
							disabled={Boolean(editing)}
							placeholder="요일 선택"
							options={WEEKDAY_OPTIONS}
						/>
					</Form.Item>
					<Form.Item
						name="allowed_terminal_ids"
						label="추가 허용 단말기"
						rules={[
							{
								required: true,
								message: "단말기를 하나 이상 선택하세요",
							},
						]}>
						<Select
							mode="multiple"
							placeholder="단말기 선택"
							options={terminals.map((item) => ({
								value: item.id,
								label: `${item.name} (${item.terminal_id})`,
							}))}
							style={{
								width: isMobile ? "100%" : undefined,
								minWidth: isMobile ? 0 : 280,
							}}
						/>
					</Form.Item>
				</Form>
			</Modal>
		</Card>
	);
};

export default UserWeekdayTerminalAllowances;
